import { apiQuery } from '../';

export const contactApi = apiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      rejectStage: builder.mutation({
        transformResponse: (response) => response.data,
        query: ({ ids, stage, reasonId, note }) => {
          return {
            url: `/contacts/stage`,
            method: 'put',
            data: {
              ids,
              stage,
              ats_archive_reason_id: reasonId?.id,
              archive_note: note,
            },
          };
        },
        invalidatesTags: (result) => {
          if (result[0].errors?.length > 0 || result[0].errors.contact) {
            return [];
          }
          return [{ type: 'GetOutreachCandidates' }];
        },
      }),

      moveStage: builder.mutation({
        query: ({ ids, stage }) => {
          return {
            url: `/contacts/stage`,
            method: 'put',
            data: { ids, stage },
          };
        },
        invalidatesTags: ['GetOutreachCandidates'],
      }),

      createContact: builder.mutation({
        query: (payload) => {
          const {
            firstName,
            lastName,
            email,
            emails,
            linkedin,
            company,
            jobTitle: position,
            location,
            outreachId,
          } = payload;
          const params = {
            first_name: firstName,
            last_name: lastName,
            email,
            emails,
            linkedin_url: linkedin?.trim(),
            location,
            position,
          };
          if (company) {
            const companyField =
              typeof company === 'number' ? 'company_id' : 'company_name';
            params[companyField] = company;
          }
          if (outreachId) {
            params['outreach_id'] = outreachId;
          }
          const data = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v)
          );
          return {
            url: `/contacts`,
            method: 'post',
            data: data,
          };
        },
      }),

      moveOutreach: builder.mutation({
        query: ({ ids, toOutreach }) => {
          return {
            url: `/contacts/outreach`,
            method: 'put',
            data: { ids, target_outreach: toOutreach },
          };
        },
        invalidatesTags: (result, _error, { toRefetch = true }) => {
          if (toRefetch && result) {
            return [{ type: 'GetOutreachCandidates' }];
          }
          return [];
        },
      }),

      previewCandidate: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ ids, outreachId }) => {
          return {
            url: `/outreaches/${outreachId}/candidates/preview`,
            method: 'get',
            params: { contact_ids: ids },
          };
        },
      }),

      getContactById: builder.query({
        providesTags: (result, error, { contactId }) => [
          { type: 'Contact', id: String(contactId) },
        ],
        transformResponse: (response) => response.data?.contact,
        query: ({ contactId }) => {
          return {
            method: 'get',
            url: `/contacts/${contactId}`,
          };
        },
      }),

      getContactMutualConnection: builder.query({
        transformResponse: (response) => response.data.map(({ user }) => user),
        query: ({ contactId }) => {
          return {
            method: 'get',
            url: `/contacts/${contactId}/mutual_connections`,
          };
        },
      }),

      getContact: builder.query({
        providesTags: ['GetContacts'],
        transformResponse: (response) => response?.data,
        query: ({
          filters,
          searchTerm,
          sort,
          page,
          currUser,
          extendedField = true,
        }) => {
          const { element = 'tst', order = 'desc' } = sort;
          const data = {
            extended_fields: extendedField,
            sort_element: element,
            sort_order: order,
            limit: 50,
            offset: page * 50,
          };

          if (searchTerm) {
            data.name = searchTerm;
          }
          const {
            company = [],
            location = [],
            employeeRange = [],
            industry = [],
            jobTitle = [],
            tags = [],
            skills = [],
            contactStage = [],
            outreach = [],
            user = [],
            topTalent = false,
            sourcedBy = [],
            outreachStatus = {},
            pastCompany = [],
          } = filters;

          if (user[0]) {
            if (user[0]?.id === 'all') {
              // data.user_ids = [];
            } else {
              data.user_ids = [user[0]?.id];
            }
          } else {
            data.user_ids = [currUser?.id];
          }

          if (sourcedBy?.length) {
            data.outreach_sourced_by = sourcedBy;
          }

          if (topTalent) {
            data.top_talent = 0;
            data.top_talent_ids = data.user_ids;
          }

          if (company?.length) {
            data.company_id = company?.map(({ id }) => id);
          }

          if (pastCompany?.length) {
            data.past_company_id = pastCompany?.map(({ id }) => id);
          }

          if (location?.length) {
            data.location = location?.map(({ value }) => value);
          }

          if (employeeRange?.length) {
            data.company_employee_range = employeeRange;
          }

          if (industry?.length) {
            data.company_industry = industry?.map(({ key }) => key);
          }

          if (tags?.length) {
            data.tags = tags?.map(({ id }) => id);
          }

          if (skills?.length) {
            data.skills = skills?.map(({ id }) => id);
          }

          if (jobTitle?.length) {
            data.position = jobTitle?.map(({ value }) => value);
          }

          if (contactStage?.length) {
            data.stage = contactStage;
          }

          if (outreach?.length) {
            data.outreach_ids = outreach;
          }

          if (outreachStatus?.id) {
            data.outreach_status = [outreachStatus?.id];
          }

          return {
            method: 'post',
            url: `/search/circles/contacts`,
            data,
          };
        },
      }),

      enrichEmail: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ contactId }) => {
          return {
            url: `/contacts/${contactId}/enrich`,
            method: 'put',
          };
        },
      }),

      addPhoneNumber: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ contactId, phoneNumber }) => {
          return {
            url: `/contacts/${contactId}`,
            method: 'put',
            data: {
              phone_number: phoneNumber,
            },
          };
        },
      }),

      // TODO cleanup after email enrichment feature rollout
      addEmail: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ contactId, email }) => {
          return {
            url: `/contacts/${contactId}`,
            method: 'put',
            data: {
              email,
            },
          };
        },
        invalidatesTags: (result, error, { contactId }) => [
          { type: 'Contact', id: String(contactId) },
        ],
      }),

      addEmails: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ contactId, emails }) => {
          return {
            url: `/contacts/${contactId}`,
            method: 'put',
            data: {
              emails,
            },
          };
        },
        invalidatesTags: (result, error, { contactId }) => [
          { type: 'Contact', id: String(contactId) },
        ],
      }),

      savePrimaryEmail: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ contactId, emails }) => {
          return {
            url: `/contacts/${contactId}`,
            method: 'put',
            data: {
              emails,
            },
          };
        },
        invalidatesTags: (result, error, { contactId }) => [
          { type: 'Contact', id: String(contactId) },
        ],
      }),

      editContact: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ payload, candidateId }) => {
          const {
            firstName,
            lastName,
            email,
            emails,
            linkedin,
            company,
            jobTitle: position,
            location,
            phone_number,
            phone_code,
            time_zone,
            social,
            places_city_id,
            places_country_id,
            places_state_id,
            credited_to_user_id,
            ats_candidate_source_id,
            skills,
            removeSkills = false,
            additional_info = null,
            primary_resume_id,
            deleted_contact_id,
            delete_existing,
          } = payload;
          const params = {
            first_name: firstName,
            last_name: lastName,
            email,
            emails,
            linkedin_url: linkedin,
            location,
            position,
          };
          if (phone_number !== undefined) params.phone_number = phone_number;
          if (phone_code !== undefined) params.phone_code = phone_code;
          if (time_zone !== undefined) params.time_zone = time_zone;

          if (social) params.social = social;
          // Always include place IDs even when null to allow clearing location
          if (places_city_id !== undefined)
            params.places_city_id = places_city_id;
          if (places_country_id !== undefined)
            params.places_country_id = places_country_id;
          if (places_state_id !== undefined)
            params.places_state_id = places_state_id;
          if (skills) params.skills = skills;
          if (additional_info) params.additional_info = additional_info;

          if (credited_to_user_id)
            params.credited_to_user_id = credited_to_user_id;
          if (ats_candidate_source_id)
            params.ats_candidate_source_id = ats_candidate_source_id;
          if (primary_resume_id) params.primary_resume_id = primary_resume_id;
          if (company) {
            const companyField =
              typeof company === 'number' ? 'company_id' : 'company_name';
            params[companyField] = company;
          }
          if (deleted_contact_id !== undefined)
            params.deleted_contact_id = deleted_contact_id;
          if (delete_existing !== undefined)
            params.delete_existing = delete_existing;

          // Preserve null values for place IDs and time_zone while filtering other falsy values
          const preserveFields = [
            'places_city_id',
            'places_country_id',
            'places_state_id',
            'time_zone',
          ];
          const paramsToUpdate = Object.fromEntries(
            Object.entries(params).filter(
              ([key, v]) => v || preserveFields.includes(key)
            )
          );
          if (removeSkills) paramsToUpdate.skills = '';
          return {
            url: `/contacts/${candidateId}`,
            method: 'put',
            data: paramsToUpdate,
          };
        },
        invalidatesTags: (result, error, { candidateId }) => [
          'GetOutreachCandidates',
          'GetAtsContactsResume',
          { type: 'Contact', id: String(candidateId) },
        ],
      }),

      editCandidateInfo: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ payload, candidateId }) => {
          const {
            firstName,
            lastName,
            email,
            emails,
            linkedin,
            company,
            jobTitle: position,
            location,
            phone_number,
            phone_code,
            social,
            places_city_id,
            places_country_id,
            places_state_id,
            credited_to_user_id,
            ats_candidate_source_id,
            skills,
            tags,
            removeSkills = false,
            additional_info = null,
            education,
            experience,
            time_zone,
            deleted_contact_id,
            delete_existing,
            contact_resume_id,
            PhoneCode,
          } = payload;
          const params = {
            first_name: firstName,
            last_name: lastName,
            email,
            emails,
            linkedin_url: linkedin,
            phone_number: phone_number,
            phone_code: phone_code ?? PhoneCode,
            location,
            position,
            education,
            experience,
          };
          if (phone_code !== undefined) params.phone_code = phone_code;
          if (time_zone !== undefined) params.time_zone = time_zone;

          if (social) params.social = social;
          // Always include place IDs even when null to allow clearing location
          if (places_city_id !== undefined)
            params.places_city_id = places_city_id;
          if (places_country_id !== undefined)
            params.places_country_id = places_country_id;
          if (places_state_id !== undefined)
            params.places_state_id = places_state_id;
          if (skills) params.skills = skills;
          if (tags) params.tags = tags;
          if (additional_info) params.additional_info = additional_info;
          if (education) params.education = education;
          if (experience) params.experience = experience;

          if (credited_to_user_id)
            params.credited_to_user_id = credited_to_user_id;
          if (ats_candidate_source_id)
            params.ats_candidate_source_id = ats_candidate_source_id;
          if (company) {
            const companyField =
              typeof company === 'number' ? 'company_id' : 'company_name';
            params[companyField] = company;
          }
          if (deleted_contact_id !== undefined)
            params.deleted_contact_id = deleted_contact_id;
          if (delete_existing !== undefined)
            params.delete_existing = delete_existing;
          if (contact_resume_id !== undefined)
            params.contact_resume_id = contact_resume_id;

          // Preserve null values for place IDs and time_zone while filtering other falsy values
          const preserveFields = [
            'places_city_id',
            'places_country_id',
            'places_state_id',
            'time_zone',
            'last_name',
          ];
          const paramsToUpdate = Object.fromEntries(
            Object.entries(params).filter(
              ([key, v]) => v || preserveFields.includes(key)
            )
          );
          if (removeSkills) paramsToUpdate.skills = '';
          return {
            url: `/contacts/${candidateId}`,
            method: 'put',
            data: paramsToUpdate,
          };
        },
        invalidatesTags: (result, error, { candidateId }) => [
          'GetOutreachCandidates',
          { type: 'Contact', id: String(candidateId) },
        ],
      }),

      addTags: builder.mutation({
        transformResponse: (response) => response?.data,
        query: (payload) => {
          const { tags, contactId } = payload;
          return {
            url: `/contacts/${contactId}/tags_add`,
            method: 'post',
            data: { tags: tags.join(',') },
          };
        },
      }),

      removeTags: builder.mutation({
        transformResponse: (response) => response?.data,
        query: (payload) => {
          const { tags, contactId } = payload;
          return {
            url: `/contacts/${contactId}/tags_remove`,
            method: 'post',
            data: { tags: tags.join(',') },
          };
        },
      }),

      uploadResume: builder.mutation({
        transformResponse: (response) => response.data?.contact,
        query: (payload) => {
          const { resume, contactId } = payload;
          var formdata = new FormData();
          formdata.append('resume', resume);
          return {
            url: `/contacts/${contactId}`,
            method: 'put',
            data: formdata,
          };
        },
      }),

      removeResume: builder.mutation({
        query: (payload) => {
          const { contactId } = payload;
          return {
            url: `/contacts/${contactId}/resume`,
            method: 'delete',
          };
        },
      }),

      getContactRejectionReason: builder.query({
        transformResponse: (response) => response?.data,
        query: ({ contactId }) => {
          return {
            method: 'get',
            url: `/contacts/${contactId}/rejection_reasons`,
          };
        },
      }),

      getEmailPreview: builder.query({
        transformResponse: (response) => response?.data,
        query: ({ contact_source_url }) => {
          return {
            method: 'get',
            url: `/contact_enrichments/preview?${contact_source_url}`,
          };
        },
      }),

      getEmailEnrichVerify: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ contactId }) => {
          return {
            url: `/contacts/${contactId}/enrich_verify`,
            method: 'put',
          };
        },
      }),
    };
  },
  overrideExisting: true,
});
export const {
  useMoveOutreachMutation,
  useCreateContactMutation,
  useMoveStageMutation,
  useEditContactMutation,
  useEditCandidateInfoMutation,
  useEnrichEmailMutation,
  useUploadResumeMutation,
  useAddPhoneNumberMutation,
  useAddEmailMutation,
  useAddEmailsMutation,
  useSavePrimaryEmailMutation,
  useRemoveResumeMutation,
  useGetContactByIdQuery,
  useGetContactQuery,
  useGetContactMutualConnectionQuery,
  useGetContactRejectionReasonQuery,
  useGetEmailPreviewQuery,
  useGetEmailEnrichVerifyMutation,
  useRejectStageMutation,
  useAddTagsMutation,
  useRemoveTagsMutation,
  usePreviewCandidateMutation,
} = contactApi;
